import { ReactElement, useMemo, useRef, useState } from 'react'
import { DrawControllerWrapper, TargetPlane, useTapelineSnapTargets } from '@scene'
import { find, isNull } from 'lodash-es'
import { Plane as PlaneThree, Vector3 } from 'three'
import {
  InteractiveLine,
  InteractiveLineRef,
  InteractiveLineHandles,
  DrawControllerRef,
  RectangleXYZ,
  getFlatShapeNormal,
  ImmutableVector3,
  TransientDrawState,
  toImmutable,
} from '@modugen/ui-toolkit'
import { useModelStore, useControlStore } from '@editorStores'
import { useClickActionEnabled } from '@editorHooks'
import { nthLooped } from '@utils'
import { useModelSnapTargets, useWallOpeningsSnapTargets } from '../FloorplanDrawer/hooks'

interface Props {
  wallGuid: string
  openingGuid: string
  onUpdate?: (points: RectangleXYZ) => void
}

const OpeningEdit3D = ({ wallGuid, openingGuid, onUpdate }: Props): ReactElement => {
  const { walls, updateOpening } = useModelStore(state => ({
    walls: state.model.walls,
    updateOpening: state.updateOpening,
  }))

  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const drawControllerEnabled = useClickActionEnabled()
  const modelSnapTargets = useModelSnapTargets({ xyOnly: false })
  const tapelineSnapTargets = useTapelineSnapTargets(false)

  // Get wall and opening for snap targets
  const wall = useMemo(() => find(walls, { guid: wallGuid }), [walls, wallGuid]) as
    | ShapeObject
    | undefined

  const opening = useMemo(() => find(wall?.openings, { guid: openingGuid }), [wall, openingGuid])

  const openingSnapTargets = useWallOpeningsSnapTargets({
    xyOnly: false,
  })

  const snapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineSnapTargets, ...openingSnapTargets],
    [modelSnapTargets, tapelineSnapTargets, openingSnapTargets],
  )

  const edgeLineRefs = useRef<Array<InteractiveLineRef>>([])
  const drawControllerRef = useRef<DrawControllerRef>(null)

  const [activeEdgeIndex, setActiveEdgeIndex] = useState<null | number>(null)

  // MEMOS

  const wallNormal = useMemo(
    () => (wall ? getFlatShapeNormal(wall.shape.points as RectangleXYZ) : null),
    [wall],
  )

  const wallPlane = useMemo(
    () =>
      wall && opening
        ? new PlaneThree().setFromCoplanarPoints(
            wall.shape.points[0].v,
            wall.shape.points[1].v,
            wall.shape.points[2].v,
          )
        : undefined,
    [wall, opening],
  )

  // Convert opening rectangle to 4 edges
  const openingEdges = useMemo(() => {
    if (!opening) return undefined

    const points = opening.shape.points as RectangleXYZ
    return [
      [points[0], points[1]], // Edge 0: top
      [points[1], points[2]], // Edge 1: right
      [points[2], points[3]], // Edge 2: bottom
      [points[3], points[0]], // Edge 3: left
    ] as Array<[ImmutableVector3, ImmutableVector3]>
  }, [opening])

  // HANDLERS
  const moveOpeningEdge = (state: TransientDrawState) => {
    if (isNull(activeEdgeIndex) || !state.drawPoint || !wallPlane || !openingEdges) return

    const edge = edgeLineRefs.current[activeEdgeIndex]
    if (!edge?.transientLine.current) return

    // Project the draw point onto the wall plane
    const projectedPoint = toImmutable(wallPlane.projectPoint(state.drawPoint.v, new Vector3()))

    // Get the current edge
    const [edgeStart, edgeEnd] = openingEdges[activeEdgeIndex]

    // Calculate the direction of movement (perpendicular to the edge)
    const edgeDirection = edgeEnd.sub(edgeStart).normalize()
    const moveDirection = new ImmutableVector3()
      .crossVectors(wallNormal!, edgeDirection)
      .normalize()

    // Calculate the signed perpendicular distance from the edge to the projected point
    // This is the dot product of the move vector with the move direction
    const moveVector = projectedPoint.sub(edgeStart)
    const perpendicularDistance = moveVector.dot(moveDirection)

    // Update the edge
    const newEdgeStart = edgeStart.add(moveDirection.multiplyScalar(perpendicularDistance))
    const newEdgeEnd = edgeEnd.add(moveDirection.multiplyScalar(perpendicularDistance))

    edge.updateLine([newEdgeStart, newEdgeEnd])

    // Update adjacent edges
    const previousEdge = nthLooped(edgeLineRefs.current, activeEdgeIndex - 1)
    const nextEdge = nthLooped(edgeLineRefs.current, activeEdgeIndex + 1)

    // Update previous edge's end point
    if (previousEdge?.transientLine.current) {
      previousEdge.updateHandle(InteractiveLineHandles.End, newEdgeStart)
    }

    // Update next edge's start point
    if (nextEdge?.transientLine.current) {
      nextEdge.updateHandle(InteractiveLineHandles.Start, newEdgeEnd)
    }
  }

  const drawEnd = () => {
    if (!opening || !openingEdges || isNull(activeEdgeIndex)) return

    // Collect all points from the edges
    const openingPoints = edgeLineRefs.current.map((line, i) => {
      if (line?.transientLine.current) {
        return line.transientLine.current[0]
      }
      // Fallback to original edge start
      return openingEdges[i][0]
    }) as RectangleXYZ

    // Ensure points are on the wall plane
    if (wallPlane) {
      const projectedPoints = openingPoints.map(p =>
        toImmutable(wallPlane.projectPoint(p.v, new Vector3())),
      ) as RectangleXYZ

      // Update the opening
      const updatedOpening: Opening = {
        ...opening,
        shape: {
          ...opening.shape,
          points: projectedPoints,
        },
      }
      updateOpening(wallGuid, updatedOpening)
      onUpdate?.(projectedPoints)
    }

    setActiveEdgeIndex(null)
  }

  if (!wall || !opening || !wallNormal || !openingEdges) return <></>

  return (
    <>
      {/* DrawController used for moving the edges of an existing opening */}
      <DrawControllerWrapper
        ref={drawControllerRef}
        enabled={!isNull(activeEdgeIndex) && drawControllerEnabled}
        onMouseMove={moveOpeningEdge}
        onDrawEnd={drawEnd}
        enableIndicator={false}
        isValidDrawTarget={object => object.name === 'draw-plane'}
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
        additionalSnapTargets={snapTargets}
      />

      {/* Render each edge as an InteractiveLine */}
      {openingEdges.map(([start, end], i) => (
        <InteractiveLine
          key={`${openingGuid}-${i}-${start.x}-${start.y}-${start.z}-${end.x}-${end.y}-${end.z}`}
          ref={element => {
            edgeLineRefs.current[i] = element as InteractiveLineRef
          }}
          line={[start, end]}
          color={i === activeEdgeIndex ? 'blue' : 'black'}
          disableDepthTest
          isSelected={activeEdgeIndex === i}
          onClick={() => setActiveEdgeIndex(i)}
          showHandles={false}
          nonSelectable={!isNull(activeEdgeIndex)}
          clickDisabled={!isNull(activeEdgeIndex)}
          width={4}
        />
      ))}

      {/* Target plane for drawing */}
      {wallPlane && opening && (
        <TargetPlane points={opening.shape.points as RectangleXYZ} name="draw-plane" />
      )}
    </>
  )
}

export default OpeningEdit3D
